import { DataStoreQueryCondition, DataStoreQueryConditionType } from "../DataStorage/DataStoreQueryCondition";
import { IUserInfo } from "../DataStorage/IUserInfo";
import { BuilderChangeDifferences } from "./BuilderChangeDifferences";
import { BuilderChangeSet } from "./BuilderChangeSet";
import { BuilderChangeSetMergeIssue, BuilderChangeSetMergeIssues } from "./BuilderChangeSetMergeIssues";
import { BuilderChangeSetStatus } from "./BuilderChangeSetStatus";
import { BuilderCountData, BuilderProjectData } from "./BuilderProjectData";
import { BuilderProjectMembership } from "./BuilderProjectMembership";
import { BuilderStorage } from "./BuilderStorage";
import { BuilderStorageConstants } from "./BuilderStorageConstants";
import { BuilderStorageHistory } from "./BuilderStorageHistory";
import { BuilderStorageItem } from "./BuilderStorageItem";
import { BuilderStorageItemHistory } from "./BuilderStorageItemHistory";
import { BuilderTypes } from "./BuilderTypes";
import { IBuilderStorage } from "./IBuilderStorage";
import { MergeInstructions, MergeInstructionType } from "./MergeInstructions";
import { BuilderProjectType } from "./BuilderProjectType";
import { AppGlueCorePlugins } from "../../AppGlueCorePlugins";
import { AppGlueError } from "../../AppGlueError";
import { DataUtilities } from "../../Common/DataUtilities";
import { BuilderStorageCallback } from "./IBuilderStorageCallback";

export class BuilderStorageImpl implements IBuilderStorage {
    builderProjectDS = AppGlueCorePlugins.DataStoreFactory!.getDataStoreByName<BuilderProjectData>(
        BuilderProjectData.name,
    )!;
    builderProjectMembershipDS = AppGlueCorePlugins.DataStoreFactory!.getDataStoreByName<BuilderProjectMembership>(
        BuilderProjectMembership.name,
    )!;
    changeSetDS = AppGlueCorePlugins.DataStoreFactory!.getDataStoreByName<BuilderChangeSet>(BuilderChangeSet.name)!;
    builderDataDS = AppGlueCorePlugins.DataStoreFactory!.getDataStoreByName<BuilderStorage>(BuilderStorage.name)!;
    builderHistoryDataDS = AppGlueCorePlugins.DataStoreFactory!.getDataStoreByName<BuilderStorageHistory>(
        BuilderStorageHistory.name,
    )!;

    // ---------------
    // builder data
    // ---------------

    private static createBuilderStorageItem(builderStorage: BuilderStorage): BuilderStorageItem {
        let bs = new BuilderStorageItem(builderStorage.builderId, builderStorage.builderType, builderStorage.name!);
        bs.majorVersion = builderStorage.majorVersion;
        bs.minorVersion = builderStorage.minorVersion;
        bs.revision = builderStorage.revision;

        bs.createdBy = builderStorage.createdBy;
        bs.createdOn = builderStorage.createdOn;
        bs.lastSavedBy = builderStorage.lastSavedBy;
        bs.lastSavedOn = builderStorage.lastSavedOn;
        bs.changeSetName = builderStorage.changeSetName;
        bs.tags = builderStorage.tags;
        bs.id = builderStorage.id;
        bs.isNewInChangeSet = builderStorage.isNewInChangeSet;
        bs.validationBreaks = builderStorage.validationBreaks;
        bs.mergedToLiveBranch = builderStorage.mergedToLiveBranch;

        return bs;
    }

    private static createBuilderStorageItemFromHistory(
        builderStorage: BuilderStorageHistory,
    ): BuilderStorageItemHistory {
        let bs = new BuilderStorageItemHistory(
            builderStorage.builderId,
            builderStorage.builderType,
            builderStorage.name!,
        );
        bs.majorVersion = builderStorage.majorVersion;
        bs.minorVersion = builderStorage.minorVersion;
        bs.revision = builderStorage.revision;

        bs.createdBy = builderStorage.createdBy;
        bs.createdOn = builderStorage.createdOn;
        bs.lastSavedBy = builderStorage.lastSavedBy;
        bs.lastSavedOn = builderStorage.lastSavedOn;
        bs.changeSetName = builderStorage.changeSetName;
        bs.id = builderStorage.id;
        bs.notes = builderStorage.notes ?? "";
        bs.projectId = builderStorage.projectId;
        bs.teamName = builderStorage.teamName;
        bs.validationBreaks = builderStorage.oldStorage.validationBreaks;
        bs.tags = builderStorage.oldStorage.tags;
        bs.mergedToLiveBranch = builderStorage.oldStorage.mergedToLiveBranch;

        return bs;
    }

    async builderDataExists(user: IUserInfo, type: BuilderTypes, id: string, changeSetName?: string): Promise<boolean> {
        let changeSets = [BuilderStorageConstants.LIVE_CHANGE_SET_NAME];
        if (changeSetName) changeSets.push(changeSetName);

        let builderData = await this.builderDataDS.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderType", type),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderId", id),
            new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "changeSetName", changeSets),
        ]);

        return builderData.length !== 0;
    }

    // this gets builder data in change set but if its not found, it gets data in default change set.
    async getBuilderData(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        changeSetName?: string,
    ): Promise<BuilderStorage> {
        let changeSets = [BuilderStorageConstants.LIVE_CHANGE_SET_NAME];
        if (changeSetName) changeSets.push(changeSetName);

        let res = await this.builderDataDS.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderType", type),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderId", id),
            new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "changeSetName", changeSets),
        ]);

        if (res.length === 0) {
            throw new AppGlueError("Builder not found with id: " + id + " " + type, 400);
        } else if (res.length === 1) {
            return res[0];
        } else {
            // filter out default
            return res.find((s) => s.changeSetName !== BuilderStorageConstants.LIVE_CHANGE_SET_NAME)!;
        }
    }

    async moveBuilderToChangeSet(
        user: IUserInfo,
        builderType: BuilderTypes,
        builderId: string,
        currentChangeSet: string,
        newChangeSet: string,
    ): Promise<BuilderStorage> {
        // move builder
        let b = await this.getBuilderData(user, builderType, builderId, currentChangeSet);
        b.changeSetName = newChangeSet;
        await this.builderDataDS.store(user, b);

        // move history
        let h = await this.getHistory(user, builderType, builderId, currentChangeSet);

        for (let hi of h) {
            hi.changeSetName = newChangeSet;
            if (hi.oldStorage.changeSetName === currentChangeSet) hi.oldStorage.changeSetName = newChangeSet;
        }

        await this.builderHistoryDataDS.store(user, h);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderChanged(user, b.projectId, b.builderId, b.builderType);
        });

        this.updateChangeSetCount(user, currentChangeSet);
        this.updateChangeSetCount(user, newChangeSet);

        return b;
    }

    async saveBuilderData(
        user: IUserInfo,
        data: BuilderStorage,
        notes?: string,
        changeSetName?: string,
    ): Promise<BuilderStorage> {
        data.revision = data.revision + 1;

        if (changeSetName && data.changeSetName !== changeSetName) {
            let exists = await this.builderDataExists(user, data.builderType, data.builderId, changeSetName);
            if (exists) {
                let savedData = await this.getBuilderData(user, data.builderType, data.builderId, changeSetName);

                if (savedData.changeSetName !== changeSetName) {
                    // we are saving to a new branch
                    let newData = new BuilderStorage(data.builderId, data.builderType, data.name);
                    Object.assign(newData, data);
                    newData.id = DataUtilities.generateUniqueId();

                    data = newData;
                }
            }

            // check to see if change set exits.
            // if not, create it
            let changeSetExists = await this.changeSetExists(user, changeSetName);

            if (!changeSetExists) {
                await this.createChangeSet(user, changeSetName);
            } else {
                let changeSet = await this.getChangeSet(user, changeSetName);
                if (changeSet.status !== BuilderChangeSetStatus.OPEN) {
                    throw new AppGlueError("cannot save to a change set that has status of " + changeSet.status, 400);
                }
            }

            data.changeSetName = changeSetName;
        }

        await this.builderHistoryDataDS.store(user, new BuilderStorageHistory(data, notes));

        // set record if new in change set
        if (!data.changeSetName || data.changeSetName === BuilderStorageConstants.LIVE_CHANGE_SET_NAME) {
            // it is in live branch,so merged by default
            data.mergedToLiveBranch = true;
            data.isNewInChangeSet = false;
        } else {
            // this resets it when merge happens
            data.mergedToLiveBranch = false;
            if (await this.builderDataExists(user, data.builderType, data.builderId)) {
                // it exists in live
                data.isNewInChangeSet = false;
            } else {
                data.isNewInChangeSet = true;
            }
        }

        let r = await this.builderDataDS.store(user, data);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderChanged(user, r.projectId, r.builderId, r.builderType);
        });

        if (data.projectId) {
            await this.updateBuilderProjectCount(
                user,
                data.projectId,
                data.changeSetName ?? BuilderStorageConstants.LIVE_CHANGE_SET_NAME,
            );
        }

        if (data.changeSetName) {
            await this.updateChangeSetCount(user, data.changeSetName);
        }

        return r;
    }

    async deleteBuilderData(user: IUserInfo, type: BuilderTypes, id: string, changeSetName?: string): Promise<void> {
        let promises: Promise<void>[] = [];

        let r = await this.getBuilderData(user, type, id, changeSetName);

        let res = await this.builderHistoryDataDS.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderId", id),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderType", type),
        ]);

        for (let r of res) {
            promises.push(
                new Promise<void>((accept2, reject2) => {
                    this.builderHistoryDataDS
                        .delete(user, r.id!)
                        .then(() => {
                            accept2();
                        })
                        .catch((err) => reject2(err));
                }),
            );
        }

        // this deletes all builders, even in change sets
        let res2 = await this.builderDataDS.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderId", id),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderType", type),
        ]);

        for (let r of res2) {
            promises.push(
                new Promise<void>((accept2, reject2) => {
                    this.builderDataDS
                        .delete(user, r.id!)
                        .then(() => {
                            accept2();
                        })
                        .catch((err) => reject2(err));
                }),
            );
        }

        await Promise.all(promises);

        if (r.projectId) {
            await this.updateBuilderProjectCount(
                user,
                r.projectId,
                r.changeSetName ?? BuilderStorageConstants.LIVE_CHANGE_SET_NAME,
            );
        }

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderRemoved(user, r.projectId, r.builderId, r.builderType);
        });
    }

    async copyBuilderData(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        newName: string,
        changeSetName?: string,
    ): Promise<BuilderStorage> {
        let b = await this.getBuilderData(user, type, id, changeSetName);
        let newBuilder = { ...b };

        newBuilder.id = DataUtilities.generateUniqueId();
        newBuilder.builderId = DataUtilities.generateUniqueId();
        newBuilder.name = newName;
        newBuilder.revision = 0;
        newBuilder.majorVersion = 1;
        newBuilder.minorVersion = 0;
        newBuilder.changeSetName = changeSetName ?? BuilderStorageConstants.LIVE_CHANGE_SET_NAME;
        let savedB = await this.saveBuilderData(user, newBuilder);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderAdded(user, savedB.projectId, savedB.builderId, savedB.builderType);
        });

        return savedB;
    }

    // ---------------
    // change sets
    // ---------------

    // this returns a number of headers with same data
    async getRevisionHistory(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        changeSetName?: string,
    ): Promise<BuilderStorageItemHistory[]> {
        let res = await this.getHistory(user, type, id, changeSetName ?? BuilderStorageConstants.LIVE_CHANGE_SET_NAME);

        return res.map((r) => {
            return BuilderStorageImpl.createBuilderStorageItemFromHistory(r);
        });
    }

    // this assumes default change set
    async changeSetExists(user: IUserInfo, changeSetName: string): Promise<boolean> {
        return this.changeSetDS.existsByAlternateId(user, "name", changeSetName);
    }

    async checkoutBuilderToChangeSet(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        changeSetName: string,
    ): Promise<BuilderStorage> {
        let exists = await this.changeSetExists(user, changeSetName);
        if (exists) {
            let cs = await this.getChangeSet(user, changeSetName);
            if (cs.status !== BuilderChangeSetStatus.OPEN)
                throw new AppGlueError("cannot check out to a change set with status " + cs.status, 400);
        }

        let d = await this.getBuilderData(user, type, id, BuilderStorageConstants.LIVE_CHANGE_SET_NAME);

        let newData = new BuilderStorage(d.builderId, d.builderType, d.name);
        Object.assign(newData, d);
        newData.id = DataUtilities.generateUniqueId();

        newData.changeSetName = changeSetName;
        newData.id = DataUtilities.generateUniqueId();
        if (!exists) {
            await this.createChangeSet(user, changeSetName);
        }

        d = await this.saveBuilderData(user, newData, "checkout to change set: " + changeSetName, changeSetName);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderChanged(user, d.projectId, d.builderId, d.builderType);
        });

        return d;
    }

    async getChangeSet(user: IUserInfo, changeSetName: string): Promise<BuilderChangeSet> {
        return this.changeSetDS.getByAlternateId(user, "name", changeSetName);
    }

    async mergeBuilder(
        user: IUserInfo,
        changeSetName: string,
        type: BuilderTypes,
        id: string,
        majorVersion: boolean,
        deleteAfterMerge: boolean,
        notes?: string,
    ): Promise<BuilderStorage> {
        let branchData = await this.getBuilderData(user, type, id, changeSetName);

        if (branchData.changeSetName === BuilderStorageConstants.LIVE_CHANGE_SET_NAME) {
            throw new AppGlueError("Builder cannot be merged as it is in default change set");
        }

        let exists = await this.builderDataExists(user, type, id, BuilderStorageConstants.LIVE_CHANGE_SET_NAME);

        if (exists) {
            let defaultData = await this.getBuilderData(user, type, id, BuilderStorageConstants.LIVE_CHANGE_SET_NAME);

            defaultData.builderData = branchData.builderData;

            if (majorVersion) {
                defaultData.majorVersion = defaultData.majorVersion + 1;
                defaultData.minorVersion = 0;
            } else {
                defaultData.minorVersion = defaultData.minorVersion + 1;
            }

            let r = await this.saveBuilderData(
                user,
                defaultData,
                "merge from changeset: " + changeSetName + (notes ? " " + notes : ""),
            );

            if (deleteAfterMerge) {
                await this.clearBranchVersionData(user, branchData.builderType, branchData.builderId, changeSetName);
            } else {
                branchData.mergedToLiveBranch = true;
                await this.builderDataDS.store(user, branchData);
            }

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.builderChanged(user, r.projectId, r.builderId, r.builderType);
            });

            await this.updateChangeSetCount(user, changeSetName);

            return r;
        } else {
            let newData = { ...branchData };

            newData.id = DataUtilities.generateUniqueId();

            newData.majorVersion = 1;
            newData.minorVersion = 0;
            newData.changeSetName = BuilderStorageConstants.LIVE_CHANGE_SET_NAME;

            let r = await this.saveBuilderData(
                user,
                newData,
                "merge from changeset: " + changeSetName + (notes ? " " + notes : ""),
            );

            if (deleteAfterMerge) {
                await this.clearBranchVersionData(user, branchData.builderType, branchData.builderId, changeSetName);
            } else {
                branchData.mergedToLiveBranch = true;
                await this.builderDataDS.store(user, branchData);
            }

            if (branchData.projectId) {
                await this.updateBuilderProjectCount(user, branchData.projectId, changeSetName);
            }

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.builderChanged(user, r.projectId, r.builderId, r.builderType);
            });

            await this.updateChangeSetCount(user, changeSetName);

            return r;
        }
    }

    async mergeBuilderWithSpecifiedVersion(
        user: IUserInfo,
        changeSetName: string,
        type: BuilderTypes,
        id: string,
        majorVersion: number,
        minorVersion: number,
        deleteAfterMerge: boolean,
        notes?: string,
    ): Promise<BuilderStorage> {
        let branchData = await this.getBuilderData(user, type, id, changeSetName);

        if (branchData.changeSetName === BuilderStorageConstants.LIVE_CHANGE_SET_NAME)
            throw new AppGlueError("Builder cannot be merged as it is in default change set");

        let exists = await this.builderDataExists(user, type, id, BuilderStorageConstants.LIVE_CHANGE_SET_NAME);

        if (exists) {
            let defaultData = await this.getBuilderData(user, type, id, BuilderStorageConstants.LIVE_CHANGE_SET_NAME);
            defaultData.builderData = branchData.builderData;

            defaultData.majorVersion = majorVersion;
            defaultData.minorVersion = minorVersion;

            let r = await this.saveBuilderData(
                user,
                defaultData,
                "merge from changeset: " + changeSetName + (notes ? " " + notes : ""),
            );

            if (deleteAfterMerge) {
                await this.clearBranchVersionData(user, branchData.builderType, branchData.builderId, changeSetName);
            }

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.builderChanged(user, r.projectId, r.builderId, r.builderType);
            });

            return r;
        } else {
            let newData = { ...branchData };

            newData.id = DataUtilities.generateUniqueId();

            newData.majorVersion = majorVersion;
            newData.minorVersion = minorVersion;
            newData.changeSetName = BuilderStorageConstants.LIVE_CHANGE_SET_NAME;

            let r = await this.saveBuilderData(
                user,
                newData,
                "merge from changeset: " + changeSetName + (notes ? " " + notes : ""),
            );

            if (deleteAfterMerge) {
                await this.clearBranchVersionData(user, branchData.builderType, branchData.builderId, changeSetName);
            }

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.builderChanged(user, r.projectId, r.builderId, r.builderType);
            });

            return r;
        }
    }

    async mergeChangeSet(
        user: IUserInfo,
        changeSetName: string,
        majorVersion: boolean,
        instructions?: MergeInstructions[],
        notes?: string,
    ): Promise<void> {
        let cs = await this.getChangeSet(user, changeSetName);

        if (cs.status !== BuilderChangeSetStatus.OPEN) {
            throw new AppGlueError("Cannot merge into a change set that is not open", 400);
        }

        let builders = await this.getAllBuildersInChangeSet(user, changeSetName);
        let promises: Promise<void>[] = [];

        let conflicts = await this.getMergeIssuesForChangeSet(user, changeSetName);

        if (conflicts && conflicts.length !== 0) {
            if (!instructions)
                throw new AppGlueError("there were conflicts but no instructions on how to deal with", 400);

            for (let c of conflicts) {
                let i = instructions.find((ii) => {
                    return ii.id === c.id && ii.type === c.type;
                });

                if (!i) {
                    throw new AppGlueError(
                        "there were no instructions on how to handle the conflict in " + c.id + ": " + c.type,
                        400,
                    );
                }
            }
        }

        for (let b of builders) {
            if (b.changeSetName === BuilderStorageConstants.LIVE_CHANGE_SET_NAME) continue;

            if (instructions && instructions.length > 0) {
                let inst = instructions.find((i) => {
                    return i.id === b.id && i.type === b.builderType;
                });
                if (inst && inst.instruction === MergeInstructionType.EXCLUDE_FROM_MERGE) {
                    // skip this one
                    continue;
                }
            }

            let builderToMerge = b;
            promises.push(
                new Promise<void>((accept2, reject2) => {
                    this.mergeBuilder(
                        user,
                        changeSetName,
                        builderToMerge.builderType,
                        builderToMerge.builderId,
                        majorVersion,
                        false,
                        notes,
                    )
                        .then(() => {
                            return;
                        })
                        .then(() => {
                            accept2();
                        })
                        .catch((err) => reject2(err));
                }),
            );
        }

        await Promise.all(promises);

        cs.closedOnDate = new Date();
        cs.status = BuilderChangeSetStatus.CLOSED_MERGED;
        await this.changeSetDS.store(user, cs);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.changeSetMerged(user, changeSetName);
        });
    }

    async mergeChangeSetWithSpecifiedVersion(
        user: IUserInfo,
        changeSetName: string,
        majorVersionNumber: number,
        minorVersionNumber: number,
        instructions?: MergeInstructions[],
        notes?: string,
    ): Promise<void> {
        let builders = await this.getAllBuildersInChangeSet(user, changeSetName);
        let promises: Promise<void>[] = [];

        let conflicts = await this.getMergeIssuesForChangeSet(user, changeSetName);

        if (conflicts && conflicts.length !== 0) {
            if (!instructions)
                throw new AppGlueError("there were conflicts but no instructions on how to deal with", 400);

            for (let c of conflicts) {
                let i = instructions.find((ii) => {
                    return ii.id === c.id && ii.type === c.type;
                });

                if (!i) {
                    throw new AppGlueError(
                        "there were no instructions on how to handle the conflict in " + c.id + ": " + c.type,
                        400,
                    );
                }
            }
        }

        for (let b of builders) {
            if (b.changeSetName === BuilderStorageConstants.LIVE_CHANGE_SET_NAME) continue;

            if (instructions && instructions.length > 0) {
                let inst = instructions.find((i) => {
                    return i.id === b.id && i.type === b.builderType;
                });
                if (inst && inst.instruction === MergeInstructionType.EXCLUDE_FROM_MERGE) {
                    return;
                }
            }

            promises.push(
                new Promise<void>((accept2, reject2) => {
                    this.mergeBuilderWithSpecifiedVersion(
                        user,
                        changeSetName,
                        b.builderType,
                        b.builderId,
                        majorVersionNumber,
                        minorVersionNumber,
                        false,
                        notes,
                    )
                        .then(() => {
                            return;
                        })
                        .then(() => {
                            accept2();
                        })
                        .catch((err) => reject2(err));
                }),
            );
        }

        await Promise.all(promises);

        let cs = await this.getChangeSet(user, changeSetName);
        cs.closedOnDate = new Date();
        cs.status = BuilderChangeSetStatus.CLOSED_MERGED;
        cs = await this.changeSetDS.store(user, cs);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.changeSetMerged(user, changeSetName);
        });
    }

    async cancelChangeSet(user: IUserInfo, changeSetName: string): Promise<void> {
        let cs = await this.getChangeSet(user, changeSetName);
        cs.closedOnDate = new Date();
        cs.status = BuilderChangeSetStatus.CLOSED_CANCELED;
        await this.changeSetDS.store(user, cs);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.changeSetMerged(user, changeSetName);
        });
    }

    async createChangeSet(user: IUserInfo, changeSetName: string): Promise<BuilderChangeSet> {
        if (!changeSetName || changeSetName.trim() === "") throw new AppGlueError("Change set name is required");
        let exists = await this.changeSetExists(user, changeSetName);
        if (exists) {
            throw new AppGlueError("change set exists", 400);
        } else {
            let cs = new BuilderChangeSet(changeSetName);

            let savedCs = await this.changeSetDS.store(user, cs);

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.changeSetAdded(user, changeSetName);
            });

            return savedCs;
        }
    }

    async deleteChangeSet(user: IUserInfo, changeSetName: string): Promise<void> {
        let builders = await this.getAllBuildersInChangeSet(user, changeSetName);
        let promises: Promise<void>[] = [];
        for (let b of builders) {
            promises.push(
                new Promise<void>((accept2, reject2) => {
                    this.clearBranchVersionData(user, b.builderType, b.builderId, changeSetName)
                        .then(() => accept2())
                        .catch((err) => reject2(err));
                }),
            );
        }

        await Promise.all(promises);
        let cs = await this.getChangeSet(user, changeSetName);

        await this.changeSetDS.delete(user, cs.id!);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.changeSetDeleted(user, changeSetName);
        });
    }

    async getOpenChangeSets(user: IUserInfo): Promise<BuilderChangeSet[]> {
        return this.changeSetDS.query(
            user,
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "status", BuilderChangeSetStatus.OPEN),
        );
    }

    getAllChangeSets(user: IUserInfo): Promise<BuilderChangeSet[]> {
        return this.changeSetDS.getAll(user);
    }

    async getAllBuildersInChangeSet(user: IUserInfo, changeSetName: string): Promise<BuilderStorageItem[]> {
        let res = await this.builderDataDS.query(
            user,
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "changeSetName", changeSetName),
        );
        return res.map((r) => {
            return BuilderStorageImpl.createBuilderStorageItem(r);
        });
    }

    async getMergeIssuesForBuilder(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        changeSetName: string,
    ): Promise<BuilderChangeSetMergeIssue | undefined> {
        let builderExistsInMain = await this.builderDataExists(user, type, id);

        if (!builderExistsInMain) return;

        let builderInMain = await this.getBuilderData(user, type, id);
        let builderInChangeSet = await this.getBuilderData(user, type, id, changeSetName);

        if (builderInMain.builderData === undefined || builderInChangeSet.builderData === undefined) return undefined;

        let builderHistory = await this.getRevisionHistory(user, type, id, changeSetName);

        builderHistory = builderHistory.sort((a, b) => a.lastSavedOn!.getTime() - b.lastSavedOn!.getTime());

        let history = await this.builderHistoryDataDS.get(user, builderHistory[0].id!);

        let issuesObject = new BuilderChangeSetMergeIssue(
            type,
            id,
            [],
            JSON.stringify(history.oldStorage.builderData) !== JSON.stringify(builderInMain.builderData),
            builderInChangeSet.builderData,
            history.oldStorage.builderData!,
            builderInMain.builderData,
        );

        return issuesObject.unitTestFailures.length > 0 || issuesObject.conflictingMerge ? issuesObject : undefined;
    }

    async getMergeIssuesForChangeSet(user: IUserInfo, changeSetName: string): Promise<BuilderChangeSetMergeIssues[]> {
        let issues: BuilderChangeSetMergeIssues[] = [];

        let buildersInSet = await this.getAllBuildersInChangeSet(user, changeSetName);

        let promises: Promise<void>[] = [];
        for (let b of buildersInSet) {
            let curBuilder = b;
            promises.push(
                new Promise<void>((accept2, reject2) => {
                    this.getMergeIssuesForBuilder(user, curBuilder.builderType, curBuilder.builderId, changeSetName)
                        .then((i) => {
                            if (i) {
                                issues.push(
                                    new BuilderChangeSetMergeIssues(
                                        i.type,
                                        i.id,
                                        i.unitTestFailures,
                                        i.conflictingMerge,
                                    ),
                                );
                            }
                            accept2();
                        })
                        .catch((err) => reject2(err));
                }),
            );
        }

        await Promise.all(promises);

        return issues;
    }

    async getChangesForBuilder(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        changeSetName: string,
    ): Promise<BuilderChangeDifferences> {
        let builderInChangeSet = await this.getBuilderData(user, type, id, changeSetName);

        let builderHistory = await this.getRevisionHistory(user, type, id, changeSetName);

        builderHistory = builderHistory.sort((a, b) => a.lastSavedOn!.getTime() - b.lastSavedOn!.getTime());

        let history = await this.builderHistoryDataDS.get(user, builderHistory[0].id!);

        let mainConfig: object | undefined = undefined;

        let builderExistsInMain = await this.builderDataExists(user, type, id);

        if (builderExistsInMain) {
            let b = await this.getBuilderData(user, type, id);
            mainConfig = b.builderData;
        }

        let diffObj = new BuilderChangeDifferences(
            type,
            id,
            builderInChangeSet.builderData!,
            history.oldStorage.builderData!,
            mainConfig,
        );

        return diffObj;
    }

    // ---------------
    // rollback and versioning
    // ---------------

    async rollbackToRevision(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        revision: number,
        notes?: string,
        changeSetName?: string,
    ): Promise<BuilderStorage> {
        try {
            let res = await this.builderHistoryDataDS.queryForSingle(user, [
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderId", id),
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderType", type),
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "revision", revision),
                new DataStoreQueryCondition(
                    DataStoreQueryConditionType.Equals,
                    "changeSetName",
                    changeSetName ?? BuilderStorageConstants.LIVE_CHANGE_SET_NAME,
                ),
            ]);
            // store history record

            let current = await this.getBuilderData(user, type, id, changeSetName);
            current.builderData = res.oldStorage.builderData;

            let bd = await this.saveBuilderData(user, current);

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.builderChanged(user, bd.projectId, bd.builderId, bd.builderType);
            });
            return bd;
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    async incrementMajorVersion(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        changeSetName?: string,
    ): Promise<BuilderStorage> {
        let current = await this.getBuilderData(user, type, id, changeSetName);
        current.majorVersion = current.majorVersion + 1;
        current.minorVersion = 0;
        let bd = await this.saveBuilderData(user, current, "incrementing major version");

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderChanged(user, bd.projectId, bd.builderId, bd.builderType);
        });

        return bd;
    }

    async incrementMinorVersion(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        changeSetName?: string,
    ): Promise<BuilderStorage> {
        let current = await this.getBuilderData(user, type, id, changeSetName);
        current.minorVersion = current.minorVersion + 1;
        let bd = await this.saveBuilderData(user, current, "incrementing minor version");

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderChanged(user, bd.projectId, bd.builderId, bd.builderType);
        });

        return bd;
    }

    async setVersionNumber(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        majorVersion: number,
        minorVersion: number,
        changeSetName?: string,
    ): Promise<BuilderStorage> {
        let current = await this.getBuilderData(user, type, id, changeSetName);
        current.majorVersion = majorVersion;
        current.minorVersion = minorVersion;
        let bd = await this.saveBuilderData(
            user,
            current,
            "setting version number to " + majorVersion + "." + minorVersion,
        );

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderChanged(user, bd.projectId, bd.builderId, bd.builderType);
        });

        return bd;
    }

    // ---------------
    // list builder data
    // ---------------

    // these methods fetch the builders that are in specified change set as well as those that are in the default change set.
    async listBuilderDataForParent(
        user: IUserInfo,
        parentId: string,
        childTypes: BuilderTypes[],
        changeSetName?: string,
    ): Promise<BuilderStorageItem[]> {
        let changeSets = [BuilderStorageConstants.LIVE_CHANGE_SET_NAME];
        if (changeSetName) changeSets.push(changeSetName);

        if (childTypes.length === 0) {
            let res = await this.builderDataDS.query(user, [
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "parentId", parentId),
                new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "changeSetName", changeSets),
            ]);
            res = this.removeDuplicateBuilders(res);
            return res.map((r) => {
                return BuilderStorageImpl.createBuilderStorageItem(r);
            });
        } else {
            let res = await this.builderDataDS.query(user, [
                new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "builderType", childTypes),
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "parentId", parentId),
                new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "changeSetName", changeSets),
            ]);
            res = this.removeDuplicateBuilders(res);
            return res.map((r) => {
                return BuilderStorageImpl.createBuilderStorageItem(r);
            });
        }
    }

    async listBuilderData(
        user: IUserInfo,
        type: BuilderTypes[],
        changeSetName?: string,
    ): Promise<BuilderStorageItem[]> {
        let changeSets = [BuilderStorageConstants.LIVE_CHANGE_SET_NAME];
        if (changeSetName) changeSets.push(changeSetName);

        if (type.length === 0) {
            let res = await this.builderDataDS.query(user, [
                new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "changeSetName", changeSets),
            ]);
            res = this.removeDuplicateBuilders(res);
            return res.map((r) => {
                return BuilderStorageImpl.createBuilderStorageItem(r);
            });
        } else {
            let res = await this.builderDataDS.query(user, [
                new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "builderType", type),
                new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "changeSetName", changeSets),
            ]);
            res = this.removeDuplicateBuilders(res);
            return res.map((r) => {
                return BuilderStorageImpl.createBuilderStorageItem(r);
            });
        }
    }

    async listBuilderDataByProject(
        user: IUserInfo,
        projectId: string,
        type: BuilderTypes[],
        changeSetName?: string,
    ): Promise<BuilderStorageItem[]> {
        let changeSets = [BuilderStorageConstants.LIVE_CHANGE_SET_NAME];
        if (changeSetName) changeSets.push(changeSetName);

        if (type.length === 0) {
            let res = await this.builderDataDS.query(user, [
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "projectId", projectId),
                new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "changeSetName", changeSets),
            ]);
            res = this.removeDuplicateBuilders(res);
            return res.map((r) => {
                return BuilderStorageImpl.createBuilderStorageItem(r);
            });
        } else {
            let res = await this.builderDataDS.query(user, [
                new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "builderType", type),
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "projectId", projectId),
                new DataStoreQueryCondition(DataStoreQueryConditionType.InArray, "changeSetName", changeSets),
            ]);
            res = this.removeDuplicateBuilders(res);
            return res.map((r) => {
                return BuilderStorageImpl.createBuilderStorageItem(r);
            });
        }
    }

    // ---------------
    // project methods
    // ---------------

    async addMembershipToBuilderProject(
        user: IUserInfo,
        projectId: string,
        userToAdd: string,
    ): Promise<BuilderProjectMembership> {
        let exists = await this.builderProjectExistsById(user, projectId);
        if (!exists) {
            throw new AppGlueError("project does not exist", 400);
        }
        let isMember = await this.isMemberOfBuilderProject(user, projectId, user.userEmail);
        if (isMember) {
            let membership = await this.getMyMembershipForBuilderProject(user, projectId);
            if (!membership.isAdmin) {
                throw new AppGlueError("cannot add membership to a project you are not a an administrator of", 400);
            }
        } else {
            throw new AppGlueError("cannot add membership to a project you are not a member of", 400);
        }

        let isMember2 = await this.isMemberOfBuilderProject(user, projectId, userToAdd);
        if (isMember2) {
            throw new AppGlueError("user is already a member of this project", 400);
        } else {
            let p = await this.builderProjectMembershipDS.store(
                user,
                new BuilderProjectMembership(userToAdd, projectId),
            );

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.builderProjectChanged(user, projectId);
            });

            return p;
        }
    }

    async removeMembershipToBuilderProject(user: IUserInfo, projectId: string, userToRemove: string): Promise<void> {
        let exists = await this.builderProjectExistsById(user, projectId);
        if (!exists) {
            throw new AppGlueError("project does not exist", 400);
        }
        let isMember = await this.isMemberOfBuilderProject(user, projectId, user.userEmail);
        if (isMember) {
            let membership = await this.getMyMembershipForBuilderProject(user, projectId);
            if (!membership.isAdmin) {
                throw new AppGlueError("cannot remove membership to a project you are not a an administrator of", 400);
            }
        } else {
            throw new AppGlueError("cannot remove membership to a project you are not a member of", 400);
        }

        let isMember2 = await this.isMemberOfBuilderProject(user, projectId, userToRemove);
        if (!isMember2) {
            throw new AppGlueError("user is not a member of this project", 400);
        } else {
            let m = await this.getMembershipForBuilderProject(user, projectId, userToRemove);
            await this.builderProjectMembershipDS.delete(user, m.id!);

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.builderProjectChanged(user, projectId);
            });
        }
    }

    async isMemberOfBuilderProject(user: IUserInfo, projectId: string, userEmail: string): Promise<boolean> {
        return new Promise<boolean>((accept, reject) => {
            this.builderProjectMembershipDS
                .query(user, [
                    new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "projectId", projectId),
                    new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "emailAddress", userEmail),
                ])
                .then((res) => {
                    accept(res.length > 0);
                })
                .catch((err) => reject(err));
        });
    }

    async getAllMembersOfBuilderProject(user: IUserInfo, projectId: string): Promise<BuilderProjectMembership[]> {
        return this.builderProjectMembershipDS.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "projectId", projectId),
        ]);
    }

    async getMyMembershipForBuilderProject(user: IUserInfo, projectId: string): Promise<BuilderProjectMembership> {
        return this.getMembershipForBuilderProject(user, projectId, user.userEmail);
    }

    async getMembershipForBuilderProject(
        user: IUserInfo,
        projectId: string,
        emailAddress: string,
    ): Promise<BuilderProjectMembership> {
        return new Promise<BuilderProjectMembership>((accept, reject) => {
            this.builderProjectMembershipDS
                .query(user, [
                    new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "projectId", projectId),
                    new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "emailAddress", emailAddress),
                ])
                .then((res) => {
                    if (res.length > 0) {
                        if (res.length > 1) reject("more than one membership record exists for this user");
                        return accept(res[0]);
                    } else {
                        reject("membership not found for this user");
                    }
                })
                .catch((err) => reject(err));
        });
    }

    async setAdminInBuilderProject(user: IUserInfo, projectId: string, emailAddress: string): Promise<void> {
        let exists = await this.builderProjectExistsById(user, projectId);
        if (!exists) {
            throw new AppGlueError("project does not exist", 400);
        }
        let isMember = await this.isMemberOfBuilderProject(user, projectId, user.userEmail);
        if (isMember) {
            let membership = await this.getMyMembershipForBuilderProject(user, projectId);
            if (!membership.isAdmin) {
                throw new AppGlueError("cannot grant admin access to a project you are not a an administrator of", 400);
            }
        } else {
            throw new AppGlueError("cannot grant admin access to a project you are not a member of", 400);
        }
        let isProposedMember = await this.isMemberOfBuilderProject(user, projectId, emailAddress);
        if (!isProposedMember) {
            throw new AppGlueError("user is not a member of this project", 400);
        } else {
            let m = await this.getMembershipForBuilderProject(user, projectId, emailAddress);
            m.isAdmin = true;
            await this.builderProjectMembershipDS.store(user, m);

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.builderProjectChanged(user, projectId);
            });

            return;
        }
    }

    async removeAdminBuilderProject(user: IUserInfo, projectId: string, emailAddress: string): Promise<void> {
        let exists = await this.builderProjectExistsById(user, projectId);
        if (!exists) {
            throw new AppGlueError("project does not exist", 400);
        }
        let isMember = await this.isMemberOfBuilderProject(user, projectId, user.userEmail);
        if (isMember) {
            let membership = await this.getMyMembershipForBuilderProject(user, projectId);
            if (!membership.isAdmin) {
                throw new AppGlueError("cannot grant admin access to a project you are not a an administrator of", 400);
            }
        } else {
            throw new AppGlueError("cannot grant admin access to a project you are not a member of", 400);
        }
        let proposedIsMember = await this.isMemberOfBuilderProject(user, projectId, emailAddress);
        if (!proposedIsMember) {
            throw new AppGlueError("user is not a member of this project", 400);
        } else {
            let m = await this.getMembershipForBuilderProject(user, projectId, emailAddress);
            m.isAdmin = false;
            await this.builderProjectMembershipDS.store(user, m);

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.builderProjectChanged(user, projectId);
            });

            return;
        }
    }

    async getBuilderProjects(user: IUserInfo): Promise<BuilderProjectData[]> {
        // todo: need to check membership
        let res = await this.builderProjectMembershipDS.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "emailAddress", user.userEmail),
        ]);
        let promises: Promise<void>[] = [];
        let projects: BuilderProjectData[] = [];
        for (let m of res) {
            promises.push(
                new Promise<void>((accept2, reject2) => {
                    this.getBuilderProjectById(user, m.projectId)
                        .then((p) => {
                            projects.push(p);
                            accept2();
                        })
                        .catch((err) => reject2(err));
                }),
            );
        }

        await Promise.all(promises);
        return projects;
    }

    async deleteBuilderProject(user: IUserInfo, projectId: string): Promise<void> {
        let promises: Promise<void>[] = [];
        let res = await this.builderDataDS.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "projectId", projectId),
        ]);
        for (let r of res) {
            promises.push(
                new Promise<void>((accept2, reject2) => {
                    this.deleteBuilderData(user, r.builderType, r.builderId, r.changeSetName)
                        .then(() => {
                            accept2();
                        })
                        .catch((err) => reject2(err));
                }),
            );
        }
        // delete membership
        let res2 = await this.builderProjectMembershipDS.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "projectId", projectId),
        ]);
        for (let r of res2) {
            promises.push(
                new Promise<void>((accept2, reject2) => {
                    this.removeMembershipToBuilderProject(user, projectId, r.emailAddress)
                        .then(() => {
                            accept2();
                        })
                        .catch((err) => reject2(err));
                }),
            );
        }

        let p = await this.getBuilderProjectById(user, projectId);

        promises.push(
            new Promise<void>((accept2, reject2) => {
                this.builderProjectDS
                    .delete(user, p.id!)
                    .then(() => {
                        accept2();
                    })
                    .catch((err) => reject2(err));
            }),
        );

        await Promise.all(promises);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderProjectRemoved(user, projectId);
        });

        return;
    }

    async builderProjectExists(user: IUserInfo, projectName: string): Promise<boolean> {
        return this.builderProjectDS.existsByAlternateId(user, "name", projectName);
    }

    async builderProjectExistsById(user: IUserInfo, projectId: string): Promise<boolean> {
        return this.builderProjectDS.existsByAlternateId(user, "projectId", projectId);
    }

    async saveBuilderProjectData(user: IUserInfo, data: BuilderProjectData): Promise<BuilderProjectData> {
        let retData = this.builderProjectDS.store(user, data);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderProjectChanged(user, data.projectId);
        });

        return retData;
    }

    async addBuilderProject(
        user: IUserInfo,
        projectName: string,
        isRuleSet: boolean,
        projectPath?: string,
    ): Promise<BuilderProjectData> {
        let builderProject = new BuilderProjectData(projectName);
        builderProject.projectId = DataUtilities.generateUniqueId();
        builderProject.path = projectPath ?? "/builders";
        builderProject.projectType = isRuleSet ? BuilderProjectType.RULE_SET : BuilderProjectType.BUILDER_PROJECT;

        let exists = await this.builderProjectExists(user, projectName);
        if (exists) throw new AppGlueError("builder project " + projectName + " already exists.", 400);
        let p = await this.builderProjectDS.store(user, builderProject);

        let builderMembership = new BuilderProjectMembership(user.userEmail, builderProject.projectId);
        builderMembership.isAdmin = true;
        await this.builderProjectMembershipDS.store(user, builderMembership);

        // add input and output definitions if rule set

        if (isRuleSet) {
            await this.ensureRuleSetData(user, builderProject);
        }

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderProjectAdded(user, p.projectId);
        });

        return p;
    }

    async getBuilderProject(user: IUserInfo, projectName: string): Promise<BuilderProjectData> {
        let project = await this.builderProjectDS.getByAlternateId(user, "name", projectName);

        if (project.projectType === BuilderProjectType.RULE_SET) {
            await this.ensureRuleSetData(user, project);
        }

        return project;
    }

    async getBuilderProjectById(user: IUserInfo, projectId: string): Promise<BuilderProjectData> {
        let project = await this.builderProjectDS.getByAlternateId(user, "projectId", projectId);

        if (project.projectType === BuilderProjectType.RULE_SET) {
            await this.ensureRuleSetData(user, project);
        }

        return project;
    }

    async copyBuilderProject(user: IUserInfo, projectId: string, newName: string): Promise<BuilderProjectData> {
        let oldproject = await this.getBuilderProjectById(user, projectId);
        let project = new BuilderProjectData(newName);
        project.id = DataUtilities.generateUniqueId();
        project.projectId = DataUtilities.generateUniqueId();
        project.projectType = oldproject.projectType;

        await this.builderProjectDS.store(user, project);
        // copy over all configurations
        let data = await this.listBuilderDataByProject(user, projectId, []);
        if (data && data.length !== 0) {
            let promises: Promise<void>[] = [];

            for (let d of data) {
                promises.push(
                    new Promise<void>((accept2, reject2) => {
                        this.copyBuilderDataToNewProject(user, d.builderType, d.builderId!, project.projectId)
                            .then(() => accept2())
                            .catch((err) => reject2(err));
                    }),
                );
            }

            await Promise.all(promises);
        }

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderProjectAdded(user, project.projectId);
        });

        return project;
    }

    async convertToRuleSet(user: IUserInfo, projectId: string): Promise<BuilderProjectData> {
        let p = await this.getBuilderProjectById(user, projectId);
        p.projectType = BuilderProjectType.RULE_SET;
        await this.saveBuilderProjectData(user, p);

        BuilderStorageCallback.callbacks.forEach((c) => {
            c.builderProjectChanged(user, projectId);
        });

        return p;
    }

    async copyBuilderDataToNewProject(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        projectId: string,
        changeSetName?: string,
    ): Promise<BuilderStorage> {
        let exists = await this.builderProjectExistsById(user, projectId);
        if (!exists) throw new AppGlueError("builder project does not exist", 400);
        let b = await this.getBuilderData(user, type, id, changeSetName);
        let newBuilder = { ...b };
        newBuilder.id = DataUtilities.generateUniqueId();
        newBuilder.builderId = DataUtilities.generateUniqueId();
        newBuilder.projectId = projectId;
        newBuilder.revision = 0;
        newBuilder.majorVersion = 1;
        newBuilder.minorVersion = 0;
        newBuilder.changeSetName = changeSetName ?? BuilderStorageConstants.LIVE_CHANGE_SET_NAME;

        let project = await this.builderProjectDS.getByAlternateId(user, "projectId", projectId);

        if (b.builderId === b.projectId + BuilderStorageConstants.RULE_SET_INPUT_SUFFIX) {
            newBuilder.name = "Rule Set: " + project.name + " Input Data";
            newBuilder.builderId = projectId + BuilderStorageConstants.RULE_SET_INPUT_SUFFIX;
        }

        if (b.builderId === b.projectId + BuilderStorageConstants.RULE_SET_OUTPUT_SUFFIX) {
            newBuilder.name = "Rule Set: " + project.name + " Result Data";
            newBuilder.builderId = projectId + BuilderStorageConstants.RULE_SET_OUTPUT_SUFFIX;
        }

        if (b.builderType === BuilderTypes.RuleSetData) {
            newBuilder.name = "Rule Set Data: " + project.name;
            newBuilder.builderId = projectId;
        }

        let bb = await this.saveBuilderData(user, newBuilder);

        return bb;
    }

    async moveBuilderToProject(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        projectId: string,
    ): Promise<BuilderStorage> {
        let exists = await this.builderDataExists(user, type, id);
        if (!exists) {
            throw new AppGlueError("builder data does not exist", 400);
        }

        exists = await this.builderProjectExistsById(user, projectId);
        if (!exists) {
            throw new AppGlueError("builder project does not exist", 400);
        }

        // move all builder data
        let res = await this.builderDataDS.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderId", id),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderType", type),
        ]);

        let promises: Promise<void>[] = [];

        for (let r of res) {
            r.projectId = projectId;

            let toSave = r;
            promises.push(
                new Promise<void>((accept2, reject2) => {
                    this.saveBuilderData(user, toSave, "moving builder to project " + projectId, r.changeSetName)
                        .then(() => {
                            accept2();
                        })
                        .catch((err) => reject2(err));
                }),
            );
        }

        // fix all history
        this.builderHistoryDataDS
            .query(user, [
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderId", id),
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderType", type),
            ])
            .then((hres) => {
                for (let h of hres) {
                    let toSave = h;
                    h.oldStorage.projectId = projectId;
                    promises.push(
                        new Promise<void>((accept2, reject2) => {
                            this.builderHistoryDataDS
                                .store(user, toSave)
                                .then(() => {
                                    accept2();
                                })
                                .catch((err) => reject2(err));
                        }),
                    );
                }
            });

        await Promise.all(promises);

        let p = await this.getBuilderData(user, type, id);
        return p;
    }

    async getRevision(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        revision: number,
        changeSetName?: string,
    ): Promise<BuilderStorageHistory> {
        let h = await this.getHistory(user, type, id, changeSetName ?? BuilderStorageConstants.LIVE_CHANGE_SET_NAME);

        let hi = h.find((i) => i.revision === revision);

        if (!hi || hi === null) throw new AppGlueError("Could not find history with revision " + revision);

        return hi;
    }

    private async getHistory(
        user: IUserInfo,
        type: BuilderTypes,
        builderId: string,
        changeSetName: string,
    ): Promise<BuilderStorageHistory[]> {
        let res = await this.builderHistoryDataDS.query(user, [
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderId", builderId),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderType", type),
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "changeSetName", changeSetName),
        ]);
        return res;
    }

    private async updateBuilderProjectCount(
        user: IUserInfo,
        builderProjectId: string,
        changeSetName: string,
    ): Promise<void> {
        if (!builderProjectId) return;

        let e = await this.builderProjectDS.existsByAlternateId(user, "projectId", builderProjectId);

        if (!e) return;

        let p = await this.getBuilderProjectById(user, builderProjectId);
        let builders = await this.listBuilderDataByProject(user, builderProjectId, [], changeSetName);

        let countObj = p.builderCount.find((b) => {
            return b.changeSetName === changeSetName;
        });

        if (!countObj) {
            let countO = new BuilderCountData();
            countO.changeSetName = changeSetName;
            countO.count = builders.length;
            p.builderCount.push(countO);
            await this.saveBuilderProjectData(user, p);
        } else {
            if (countObj.count !== builders.length) {
                countObj.count = builders.length;
                await this.saveBuilderProjectData(user, p);
            }
        }
    }

    private async updateChangeSetCount(user: IUserInfo, changeSetName: string): Promise<void> {
        if (changeSetName === BuilderStorageConstants.LIVE_CHANGE_SET_NAME) return;

        let csExists = await this.changeSetDS.existsByAlternateId(user, "name", changeSetName);

        if (!csExists) return;

        let p = await this.getChangeSet(user, changeSetName);

        let builders = await this.getAllBuildersInChangeSet(user, changeSetName);

        let changed: boolean = false;

        if (p.count !== builders.length) {
            p.count = builders.length;
            changed = true;
        }

        let countUnmerged = builders.filter((b) => {
            return !b.mergedToLiveBranch;
        }).length;

        if (p.countUnmerged !== countUnmerged) {
            p.countUnmerged = countUnmerged;
            changed = true;
        }

        if (changed) {
            await this.changeSetDS.store(user, p);

            BuilderStorageCallback.callbacks.forEach((c) => {
                c.changeSetUpdated(user, changeSetName);
            });
        }
    }

    // helper methods
    private removeDuplicateBuilders(data: BuilderStorage[]): BuilderStorage[] {
        let output = data.filter((d) => d.changeSetName !== BuilderStorageConstants.LIVE_CHANGE_SET_NAME);

        for (let o of data) {
            if (o.changeSetName === BuilderStorageConstants.LIVE_CHANGE_SET_NAME) {
                if (
                    output.find((possible) => {
                        return possible.builderId === o.builderId && possible.builderType === o.builderType;
                    }) === undefined
                ) {
                    output.push(o);
                }
            }
        }

        return output;
    }

    private clearBranchVersionData(
        user: IUserInfo,
        type: BuilderTypes,
        id: string,
        changeSetName: string,
    ): Promise<void> {
        return new Promise<void>((accept, _reject) => {
            this.getBuilderData(user, type, id, changeSetName).then((bd) => {
                if (bd.changeSetName === changeSetName) {
                    this.builderDataDS.delete(user, bd.id!).then(() => {
                        this.builderHistoryDataDS
                            .query(user, [
                                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderId", id),
                                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "builderType", type),
                                new DataStoreQueryCondition(
                                    DataStoreQueryConditionType.Equals,
                                    "changeSetName",
                                    changeSetName,
                                ),
                            ])
                            .then((res) => {
                                let promises: Promise<void>[] = [];
                                for (let r of res) {
                                    promises.push(
                                        new Promise<void>((accept2) => {
                                            this.builderHistoryDataDS.delete(user, r.id!).then(() => {
                                                accept2();
                                            });
                                        }),
                                    );
                                }

                                if (bd.projectId) {
                                    promises.push(
                                        new Promise<void>((accept2) => {
                                            this.updateBuilderProjectCount(user, bd.projectId!, changeSetName).then(
                                                () => {
                                                    accept2();
                                                },
                                            );
                                        }),
                                    );
                                }

                                Promise.all(promises).then(() => {
                                    accept();
                                });
                            });
                    });
                }
            });
        });
        // todo: implement
    }

    private async ensureRuleSetData(user: IUserInfo, builderProject: BuilderProjectData): Promise<void> {
        if (builderProject.projectType === BuilderProjectType.RULE_SET) {
            let inputExists = await this.builderDataExists(
                user,
                BuilderTypes.DataDefinition,
                builderProject.projectId + BuilderStorageConstants.RULE_SET_INPUT_SUFFIX,
            );

            if (!inputExists) {
                let bs = new BuilderStorage(
                    builderProject.projectId + BuilderStorageConstants.RULE_SET_INPUT_SUFFIX,
                    BuilderTypes.DataDefinition,
                    "Rule Set: " + builderProject.name + " Input Data",
                );
                bs.builderData = {};
                bs.projectId = builderProject.projectId;
                await this.saveBuilderData(user, bs);
            }

            let outputExists = await this.builderDataExists(
                user,
                BuilderTypes.DataDefinition,
                builderProject.projectId + BuilderStorageConstants.RULE_SET_OUTPUT_SUFFIX,
            );

            if (!outputExists) {
                let bs = new BuilderStorage(
                    builderProject.projectId + BuilderStorageConstants.RULE_SET_OUTPUT_SUFFIX,
                    BuilderTypes.DataDefinition,
                    "Rule Set: " + builderProject.name + " Result Data",
                );
                bs.builderData = {};
                bs.projectId = builderProject.projectId;
                await this.saveBuilderData(user, bs);
            }

            let ruleSetDataExists = await this.builderDataExists(
                user,
                BuilderTypes.RuleSetData,
                builderProject.projectId,
            );

            if (!ruleSetDataExists) {
                let bs = new BuilderStorage(
                    builderProject.projectId,
                    BuilderTypes.RuleSetData,
                    "Rule Set Data: " + builderProject.name,
                );
                bs.builderData = {};
                bs.projectId = builderProject.projectId;
                await this.saveBuilderData(user, bs);
            }
        }
    }
}
